// Where a forecast's multi-value ("multiway") OUTCOME SPACE lives, and how to keep it.
//
// The outcome space is the question's SHAPE — the buckets an answer is allowed to name.
// It is authored once (by the contract server's preprocess) and then belongs to the
// contract, not to any one run of it: it is not recoverable from the question text, so
// nothing downstream can re-derive it. Every re-run of a multi-value question therefore
// has to be TOLD the space, or the forecast server re-authors the question as binary and
// the forecast silently changes shape.
//
// Two things went wrong because that fact was re-implemented per call site instead of
// shared:
//   • the manual reforecast's BRANCH fan-out (`commands/reforecast.ts`) never sent the
//     space, so every branch of a multi-value root was re-run as binary;
//   • the async (scheduled) reforecast promote (`reforecastPickup.ts`) replaced a live
//     multi-value contract with a BINARY vintage — a `User#` contract row then carried no
//     outcome-space cell (the contract server stamps one now), so the data-plane
//     scheduler re-forecast it as binary — which
//     turned an already-correct multiway forecast into a binary one with no user action.
//
// Pure module (no React, no store) — the read is the same one `extractMultiway` does, so
// a forecast that RENDERS as a distribution always resolves a space here.

/** `kind` of a two-slot BooleanSpace — a BINARY forecast written as a PMF, never multiway. */
const BOOLEAN_KIND = "boolean";

/**
 * The outcome space a contract carries VERBATIM — a BooleanSpace included.
 *
 * Same nested-then-top-level precedence as `outcomeSpaceOf`, and deliberately without its
 * BooleanSpace rejection: routing decisions want "is this multi-value?" (so boolean → null
 * is right there), while a DISPLAY of what the backend committed to has to tell a boolean
 * space apart from no space at all — one says "the server answered this as binary", the
 * other says "this row predates the field". Collapsing both to null would print a claim
 * nobody made.
 *
 * @param {any} contract a contract object (non-objects yield null)
 * @returns {Record<string, any> | null}
 */
export function rawOutcomeSpaceOf(contract) {
  if (!contract || typeof contract !== "object") return null;
  const raw = contract.forecast_value?.outcome_space ?? contract.outcome_space ?? null;
  return raw && typeof raw === "object" ? raw : null;
}

/**
 * The multi-value outcome space a contract commits to, or `null` for a binary one.
 *
 * Newer contracts nest the space under `forecast_value` (what the forecast server embeds
 * and chatboard persists whole); older ones carry a top-level `outcome_space`. The
 * top-level form is ALSO how a space survives a rerun that came back binary — see
 * `withOutcomeSpace` — so it is a live fallback, not only a legacy one.
 *
 * A BooleanSpace is rejected for the same reason `extractMultiway` rejects it: it is a
 * binary answer expressed as its two-slot PMF, and routing it onto the distribution lane
 * would turn every binary forecast multi-value.
 *
 * @param {any} contract a contract object (non-objects yield null)
 * @returns {Record<string, any> | null}
 */
export function outcomeSpaceOf(contract) {
  if (!contract || typeof contract !== "object") return null;
  const nested = contract.forecast_value?.outcome_space;
  if (nested && typeof nested === "object" && nested.kind !== BOOLEAN_KIND) return nested;
  const topLevel = contract.outcome_space;
  if (topLevel && typeof topLevel === "object" && topLevel.kind !== BOOLEAN_KIND) return topLevel;
  return null;
}

/**
 * The outcome space for a RUN: its live contract first, then its history vintages,
 * newest first.
 *
 * The history fallback is what makes this SELF-HEALING. A reforecast replaces the live
 * contract wholesale (`commands/reforecast.ts`) while pushing the superseded contract
 * into `run.history` — so once anything has landed one binary vintage on a multi-value
 * root, the live contract no longer knows the shape but the snapshot behind it still
 * does. Without this, a single bad rerun made the root binary FOREVER: every later
 * reforecast read the corrupted live contract, found no space, and asked the server to
 * re-author the question from bare text again.
 *
 * @param {any} run a run-store run (`{ contract, history }`)
 * @returns {Record<string, any> | null}
 */
export function outcomeSpaceOfRun(run) {
  if (!run || typeof run !== "object") return null;
  const live = outcomeSpaceOf(run.contract);
  if (live) return live;
  const history = Array.isArray(run.history) ? run.history : [];
  for (let i = history.length - 1; i >= 0; i--) {
    const fromSnapshot = outcomeSpaceOf(history[i]?.contract);
    if (fromSnapshot) return fromSnapshot;
  }
  return null;
}

/**
 * Stamp `space` onto `contract` as a top-level `outcome_space`, but ONLY when the
 * contract carries no space of its own.
 *
 * This preserves the question's IDENTITY across a rerun that degraded to binary, and
 * deliberately nothing else: `forecast_value` (the mass — the ANSWER) is never
 * fabricated or carried forward, so a stamped contract still renders exactly what the
 * latest run actually returned. What it buys is that the NEXT request — a reforecast, or
 * a branch reading its parent's contract — can still be told the space instead of
 * re-classifying the text.
 *
 * Returns `contract` unchanged (same reference) when there is nothing to add, so a
 * binary forecast's contract stays byte-identical to before.
 *
 * @param {any} contract
 * @param {Record<string, any> | null | undefined} space
 * @returns {any}
 */
export function withOutcomeSpace(contract, space) {
  if (!space || !contract || typeof contract !== "object") return contract;
  if (outcomeSpaceOf(contract)) return contract;
  return { ...contract, outcome_space: space };
}

/**
 * Would promoting `incoming` over `live` silently DROP the question's outcome space?
 *
 * True only in the one asymmetric case: the live contract is multi-value and the
 * incoming one is not. A binary vintage of a multi-value question is not a fresher
 * answer to the same question — it is an answer to a differently-shaped one — so the
 * caller's right move is to refuse the promote and keep the forecast it already has,
 * rather than replace a correct distribution with a `P(YES)`.
 *
 * @param {any} live the contract currently on the run
 * @param {any} incoming the contract the caller is about to promote
 * @returns {boolean}
 */
export function wouldDowngradeOutcomeSpace(live, incoming) {
  return !!outcomeSpaceOf(live) && !outcomeSpaceOf(incoming);
}

const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Is `incoming` a forecast over a different set of outcomes than `live` (a candidate joined
 * or dropped out, the bins were re-cut)?
 *
 * True only on a definite conflict between two multi-value spaces: another kind, other
 * labels, other bin edges, or another `allow_other`. A field only one side states is no
 * conflict.
 *
 * @param {any} live the contract currently on the run
 * @param {any} incoming the contract the caller is about to promote
 * @returns {boolean}
 */
export function wouldChangeOutcomeSpace(live, incoming) {
  const a = outcomeSpaceOf(live);
  const b = outcomeSpaceOf(incoming);
  if (!a || !b) return false;
  if (a.kind !== b.kind) return true;
  // Categorical outcomes travel as `labels` (canonical) or `outcomes` (the contract wire).
  const la = a.labels ?? a.outcomes;
  const lb = b.labels ?? b.outcomes;
  if (Array.isArray(la) && Array.isArray(lb) && !sameList(la, lb)) return true;
  const ea = a.binning?.edges;
  const eb = b.binning?.edges;
  if (Array.isArray(ea) && Array.isArray(eb) && !sameList(ea, eb)) return true;
  return (
    typeof a.allow_other === "boolean" &&
    typeof b.allow_other === "boolean" &&
    a.allow_other !== b.allow_other
  );
}

/**
 * Would the live contract's outcome names land on the wrong bars of `incoming`? Either the
 * outcome spaces differ (wouldChangeOutcomeSpace), or the live contract names its outcomes only
 * in `outcome_labels` (an older row with no space) and the incoming ones are named otherwise, or
 * number otherwise. False when `incoming` brings its own `outcome_labels`, which it keeps.
 *
 * @param {any} live the contract currently on the run
 * @param {any} incoming the contract the caller is about to show or promote
 * @returns {boolean}
 */
export function wouldRelabelOutcomes(live, incoming) {
  if (wouldChangeOutcomeSpace(live, incoming)) return true;
  const liveLabels = Array.isArray(live?.outcome_labels) ? live.outcome_labels : null;
  if (!liveLabels || Array.isArray(incoming?.outcome_labels)) return false;
  const space = outcomeSpaceOf(incoming);
  const named = space?.labels ?? space?.outcomes;
  if (Array.isArray(named)) {
    return !sameList(liveLabels, space.allow_other ? [...named, "other"] : named);
  }
  const mass = incoming?.forecast_value?.representation?.mass;
  return Array.isArray(mass) && mass.length !== liveLabels.length;
}
